/*
 * Copyright (c) 2025 Amazon.com, Inc. or its affiliates. All rights reserved.
 *
 * PROPRIETARY/CONFIDENTIAL. USE IS SUBJECT TO LICENSE TERMS.
 */

import {KatGridItem, KatIcon} from '@amzn/katal-react';
import * as styles from './styles/PhotoGridItem.module.scss';
import {useId} from 'react';
import {useObjectUrl} from 'src/utils/useObjectUrl';
import {useBundle} from '@amzn/react-arb-tools';
import {HOMEPAGE_MESSAGE_BUNDLE_NAME} from 'src/constants';
import {t} from 'src/helpers';
import {isImage} from '../photoForm/validation';
import {TEST_IDS} from 'src/testing/TestIds';
import {FileInput, type FileInputProps} from 'src/photoSelection/FileInput';

type PhotoPreviewItemProps = {
  /** The file to preview. */
  file: File;
  /** Callback to invoke when the remove icon is clicked. */
  onRemove?: () => void;
};

export const PhotoPreviewItem = ({file, onRemove}: PhotoPreviewItemProps) => {
  const [bundle] = useBundle(HOMEPAGE_MESSAGE_BUNDLE_NAME);
  const url = useObjectUrl(file);

  const mainContent = (() => {
    if (isImage(file) && url !== undefined) {
      return <img className={styles.photoPreview} src={url} alt={file.name} />;
    } else if (!isImage(file)) {
      return (
        <KatIcon
          className={styles.photoIcon}
          name="broken_image"
          size="small"
          data-testid={TEST_IDS.PhotoPreviewItem.UnsupportedFiletypeIcon}
        />
      );
    } else {
      return null;
    }
  })();

  return (
    <KatGridItem className={styles.container}>
      {mainContent}
      <div className={styles.controlsOverlay}>
        <KatIcon
          className={styles.controlIcon}
          name="remove_circle"
          size="small"
          onClick={onRemove}
          katAriaLabel={t(bundle, 'remove_icon_label', {fileName: file.name})}
        />
      </div>
    </KatGridItem>
  );
};

/**
 * Displays a file input for the photo selection grid. Acts as an HTML input
 * element with a pre-configured label.
 */
export const PhotoInputItem = (props: FileInputProps) => {
  const [bundle] = useBundle(HOMEPAGE_MESSAGE_BUNDLE_NAME);
  const id = useId();

  return (
    <KatGridItem className={styles.container}>
      <div className={styles.controlsOverlay}>
        <KatIcon
          className={styles.controlIcon}
          name="add_circle"
          size="small"
        />
      </div>
      <FileInput id={id} aria-label={t(bundle, 'add_photo')} {...props} />
    </KatGridItem>
  );
};
